/**
 * Draft & Publish Workspace Vuex Module
 */

import axios from "@/plugins/axios";

const state = {
  draftChanges: [],
  validationBlockers: [],
  validationChecklist: [],
  botSuggestions: [],
  dismissedSuggestionIds: [],
  focusedChangeId: null,
  activeTab: "changes",
  activeFilter: "all",
  // Secondary filter dimension: the entity/area a change touches
  // (question, widget, answer_options, help_text, display_label, stage,
  //  screen, placement, ordering, inclusion, widget_override, conditional).
  activeCategoryFilter: "all",
  expandedItems: {},
  published: false,
  loading: false,
};

const mutations = {
  SET_DRAFT_CHANGES(state, changes) {
    state.draftChanges = changes;
  },

  SET_VALIDATION_BLOCKERS(state, blockers) {
    state.validationBlockers = blockers;
  },

  SET_VALIDATION_CHECKLIST(state, checklist) {
    state.validationChecklist = checklist;
  },

  SET_BOT_SUGGESTIONS(state, suggestions) {
    state.botSuggestions = suggestions;
    state.dismissedSuggestionIds = [];
  },

  DISMISS_BOT_SUGGESTION(state, suggestionId) {
    if (!state.dismissedSuggestionIds.includes(suggestionId)) {
      state.dismissedSuggestionIds = [...state.dismissedSuggestionIds, suggestionId];
    }
  },

  SET_FOCUSED_CHANGE_ID(state, changeId) {
    state.focusedChangeId = changeId || null;
  },

  SET_ACTIVE_TAB(state, tab) {
    state.activeTab = tab;
  },

  SET_ACTIVE_FILTER(state, filter) {
    state.activeFilter = filter;
  },
  SET_ACTIVE_CATEGORY_FILTER(state, category) {
    state.activeCategoryFilter = category;
  },

  TOGGLE_EXPAND(state, itemId) {
    if (state.expandedItems[itemId]) {
      const remainingItems = { ...state.expandedItems };
      delete remainingItems[itemId];
      state.expandedItems = remainingItems;
    } else {
      state.expandedItems = {
        ...state.expandedItems,
        [itemId]: true,
      };
    }
  },

  RESET_EXPANDED(state) {
    state.expandedItems = {};
  },

  SET_PUBLISHED(state, value) {
    state.published = value;
  },

  SET_LOADING(state, value) {
    state.loading = value;
  },
};

const actions = {
  async fetchDraftPublishWorkspace({ commit }) {
    commit("SET_LOADING", true);
    try {
      const { data } = await axios.get("/admin/mapping-workspace/draft-publish");
      if (data.success) {
        commit("SET_DRAFT_CHANGES", data.draft_changes || []);
        commit("SET_VALIDATION_BLOCKERS", data.validation_blockers || []);
        commit("SET_VALIDATION_CHECKLIST", data.validation_checklist || []);
        commit("SET_BOT_SUGGESTIONS", data.bot_suggestions || []);
      }
    } finally {
      commit("SET_LOADING", false);
    }
  },

  resetPublished({ commit }) {
    commit("SET_PUBLISHED", false);
  },

  async validateDraftPublish({ commit }) {
    const { data } = await axios.post("/admin/mapping-workspace/draft-publish/validate");
    if (data.success) {
      commit("SET_VALIDATION_BLOCKERS", data.validation_blockers || []);
      commit("SET_VALIDATION_CHECKLIST", data.validation_checklist || []);
    }
    return data;
  },

  async publishDraftWorkspace({ commit, dispatch }) {
    const { data } = await axios.post("/admin/mapping-workspace/draft-publish/publish");
    if (data.success) {
      commit("SET_PUBLISHED", true);
      await dispatch("fetchDraftPublishWorkspace");
      await dispatch("questionLibrary/loadQuestions", null, { root: true });
      dispatch("showMessage", { ...data, messageType: "success", messageTitle: "Success" }, { root: true });
      return { success: true };
    }
    commit("SET_VALIDATION_BLOCKERS", data.validation_blockers || []);
    return { success: false, message: data.message };
  },

  setBotSuggestions({ commit }, suggestions) {
    commit("SET_BOT_SUGGESTIONS", suggestions);
  },

  dismissBotSuggestion({ commit }, suggestionId) {
    commit("DISMISS_BOT_SUGGESTION", suggestionId);
  },

  applyBotSuggestion({ commit, state }, suggestion) {
    commit("SET_ACTIVE_TAB", "changes");

    const filterByAction = {
      "Review Mapping": "mapping",
      "Review Draft Changes": "edited",
    };
    commit("SET_ACTIVE_FILTER", filterByAction[suggestion.action] || "all");

    let changeId = suggestion.change_id;
    if (!changeId) {
      const title = (suggestion.title || "").toLowerCase();
      const match = state.draftChanges.find((change) => {
        const questionId = (change.questionId || "").toLowerCase();
        return questionId && title.includes(questionId);
      });
      changeId = match?.id;
    }

    commit("RESET_EXPANDED");
    if (changeId) {
      commit("TOGGLE_EXPAND", changeId);
      commit("SET_FOCUSED_CHANGE_ID", changeId);
    }

    commit("DISMISS_BOT_SUGGESTION", suggestion.id);
  },

  clearFocusedChangeId({ commit }) {
    commit("SET_FOCUSED_CHANGE_ID", null);
  },

  setActiveTab({ commit }, tab) {
    commit("SET_ACTIVE_TAB", tab);
  },

  setActiveFilter({ commit }, filter) {
    commit("SET_ACTIVE_FILTER", filter);
  },

  setActiveCategoryFilter({ commit }, category) {
    commit("SET_ACTIVE_CATEGORY_FILTER", category);
  },

  toggleExpand({ commit }, itemId) {
    commit("TOGGLE_EXPAND", itemId);
  },

  resetExpanded({ commit }) {
    commit("RESET_EXPANDED");
  },
};

const getters = {
  filteredChanges: (state) => {
    // Default both filters to "all" so a missing/unset filter never hides changes.
    const typeFilter = state.activeFilter || "all";
    const categoryFilter = state.activeCategoryFilter || "all";
    return state.draftChanges.filter((change) => {
      const typeMatch = typeFilter === "all" || change.type === typeFilter;
      const categoryMatch = categoryFilter === "all" || (change.category || "question") === categoryFilter;
      return typeMatch && categoryMatch;
    });
  },

  changeCount: (state) => state.draftChanges.length,
  filteredChangeCount: (_, getters) => getters.filteredChanges.length,
  errorCount: (state) => state.validationBlockers.filter((b) => b.severity === "error").length,
  warningCount: (state) => state.validationBlockers.filter((b) => b.severity === "warning").length,
  hasValidationErrors: (_, getters) => getters.errorCount > 0,
  passedChecklistItems: (state) => state.validationChecklist.filter((item) => item.passed).length,
  failedChecklistItems: (state) => state.validationChecklist.filter((item) => !item.passed).length,
  isItemExpanded: (state) => (itemId) => Boolean(state.expandedItems[itemId]),
  activeTab: (state) => state.activeTab,
  activeFilter: (state) => state.activeFilter,
  activeCategoryFilter: (state) => state.activeCategoryFilter,
  botSuggestions: (state) => state.botSuggestions,
  visibleBotSuggestions: (state) => state.botSuggestions.filter((s) => !state.dismissedSuggestionIds.includes(s.id)),
  focusedChangeId: (state) => state.focusedChangeId,
  draftChanges: (state) => state.draftChanges,
  validationBlockers: (state) => state.validationBlockers,
  validationChecklist: (state) => state.validationChecklist,
  published: (state) => state.published,
  loading: (state) => state.loading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
